import { PRIMARY_ASSET, type AssetId } from '@/core/project/sequence';
import { handleOf } from '@/lib/file-handles';
import { keepStorage } from '@/lib/model-cache';
import { projectDb, type ProjectDb } from '@/lib/project-db';
import { keepForProject, removeProjectRecording } from '@/lib/recording/sessions';
import { DETECTION_KINDS, scannedAssets } from '../privacy-scans';
import { onLeaveEdit, rowOf, useEditorStore, type EditorStore } from '../editor-store';
import { PROJECT_VERSION, type SavedAsset, type SavedProject } from './project';

/** Projects kept on the device; older ones go first. */
export const MAX_PROJECTS = 20;
/** Edits are written this long after the last change (and right away when the tab is hidden or the editor closes). */
const SAVE_DELAY_MS = 1200;

/**
 * Worth keeping: something that would be missed. Opening a video and looking
 * at it isn't; an edit, an import, a scan or captions are, and a recording
 * made here always is (it exists nowhere else).
 */
export function worthKeeping(state: EditorStore): boolean {
  if (state.load.status !== 'ready') return false;
  const recipe = state.history.present;
  return (
    state.recordingId !== null ||
    state.history.past.length > 0 ||
    state.assetOrder.length > 1 ||
    recipe.captions.cues.length > 0 ||
    recipe.privacy.manualTracks.length > 0 ||
    state.transcription.completed ||
    DETECTION_KINDS.some((kind) => state.privacy.scans[kind].completed)
  );
}

/** Pictures, kept as files: object URLs from this page load don't outlive it. */
type Pictures = (urls: Readonly<Record<string, string>>) => Promise<Record<string, Blob>>;

/** The project as it stands, from editor state (no I/O apart from reading this page's own picture URLs). */
export async function projectFromState(
  state: EditorStore,
  meta: { readonly id: string; readonly createdAt: number; readonly now: number; readonly poster: Blob | null },
  pictures: Pictures,
): Promise<SavedProject> {
  const row = rowOf(state);
  // Both read before the first wait, while this page's picture URLs are still alive.
  const [thumbnails, areaThumbnails] = await Promise.all([pictures(state.privacy.thumbnails), pictures(state.areas.thumbnails)]);
  const assets: SavedAsset[] = state.assetOrder.flatMap((id) => {
    const asset = state.assets[id];
    if (!asset) return [];
    const { kind, file, width, height, durationSec, video, sound, hasAudio, removed } = asset;
    // Undefined where the browser gave no handle for the file.
    const handle = handleOf(file);
    return [{ id, kind, name: file.name, size: file.size, lastModified: file.lastModified, type: file.type, width, height, durationSec, video, sound, hasAudio, removed, handle }];
  });
  return {
    version: PROJECT_VERSION,
    id: meta.id,
    name: state.media?.fileName ?? state.file?.name ?? 'video',
    createdAt: meta.createdAt,
    updatedAt: meta.now,
    durationSec: row.duration,
    clipCount: row.clips.length,
    poster: meta.poster,
    recipe: state.history.present,
    assets,
    assetOrder: state.assetOrder,
    privacy: {
      tracksByAsset: state.privacy.tracksByAsset,
      covered: state.privacy.covered,
      // Older versions read this list; they treat each video in it as scanned whole.
      scanned: scannedAssets(state.privacy.covered),
      completed: DETECTION_KINDS.filter((kind) => state.privacy.scans[kind].completed),
      precision: state.privacy.precision,
      thumbnails,
    },
    areaThumbnails,
    transcription: {
      language: state.transcription.language,
      quality: state.transcription.quality,
      spoken: state.transcription.spoken,
      captioned: state.transcription.captioned,
      completed: state.transcription.completed,
    },
    presetId: state.openedWith?.id ?? null,
    recordingId: state.recordingId,
  };
}

/** Files of projects saved during this page load: opening one again then asks for nothing. */
const sessionFiles = new Map<string, Map<AssetId, File>>();

export const filesInMemory = (projectId: string): ReadonlyMap<AssetId, File> => sessionFiles.get(projectId) ?? new Map();

export const forgetFiles = (projectId: string): void => void sessionFiles.delete(projectId);

/** Blobs behind this page's object URLs, read once each. */
const blobs = new Map<string, Promise<Blob | null>>();
const pictures: Pictures = async (urls) => {
  const entries = await Promise.all(
    Object.entries(urls).map(async ([id, url]) => {
      let blob = blobs.get(url);
      if (!blob) {
        blob = fetch(url)
          .then((response) => response.blob())
          .catch(() => null);
        blobs.set(url, blob);
      }
      return [id, await blob] as const;
    }),
  );
  return Object.fromEntries(entries.filter((entry): entry is readonly [string, Blob] => entry[1] !== null));
};

/**
 * A small picture of the opened video for the list, from the frames read for
 * the timeline. The frame is drawn right away (it is released when the editor
 * closes); only the encoding waits.
 */
function posterOf(state: EditorStore): Promise<Blob | null> {
  const frames = state.analyses[PRIMARY_ASSET]?.filmstrip ?? [];
  const frame = frames[Math.floor(frames.length / 3)];
  if (!frame || typeof OffscreenCanvas === 'undefined') return Promise.resolve(null);
  try {
    const width = 192;
    const height = Math.max(2, Math.round((frame.bitmap.height / frame.bitmap.width) * width));
    const canvas = new OffscreenCanvas(width, height);
    canvas.getContext('2d')?.drawImage(frame.bitmap, 0, 0, width, height);
    return canvas.convertToBlob({ type: 'image/jpeg', quality: 0.8 }).catch(() => null);
  } catch {
    return Promise.resolve(null);
  }
}

interface AutosaveOptions {
  readonly db?: ProjectDb;
  readonly now?: () => number;
  /** After each write (the home page's list reads it again). */
  readonly onSaved?: () => void;
}

/**
 * Keeps the open edit saved as a project while it is worked on. Returns the
 * function that stops it. `db` and `now` are swappable for tests.
 */
export function startAutosave({ db = projectDb, now = () => Date.now(), onSaved }: AutosaveOptions = {}): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pending: EditorStore | null = null;
  let poster: { readonly projectId: string; readonly blob: Blob | null } | null = null;
  let writing: Promise<void> = Promise.resolve();
  const kept = new Set<string>();
  // Whether this device keeps projects at all (not in some private windows); until known, leaving still asks.
  let storage: 'unknown' | 'works' | 'fails' = 'unknown';
  db.list().then(
    () => void (storage = storage === 'unknown' ? 'works' : storage),
    () => void (storage = 'fails'),
  );

  const save = (state: EditorStore) => {
    const id = state.projectId;
    if (!id || !worthKeeping(state)) return;
    sessionFiles.set(id, new Map(Object.values(state.assets).map((asset) => [asset.id, asset.file])));
    // Pictures and the poster are read now: closing the editor releases them right after.
    const drawn = poster?.projectId === id && poster.blob ? null : posterOf(state);
    const read = projectFromState(state, { id, createdAt: state.projectCreatedAt || now(), now: now(), poster: null }, pictures);
    // Writes go one after another, so an older state never lands after a newer one.
    writing = writing.then(async () => {
      try {
        if (drawn) poster = { projectId: id, blob: await drawn };
        const project = { ...(await read), poster: poster?.blob ?? null };
        await db.save(project);
        storage = 'works';
        const editor = useEditorStore.getState();
        if (editor.projectId === id) editor.setProjectSaved(true);
        if (state.recordingId && !kept.has(id)) {
          kept.add(id);
          await keepForProject(state.recordingId);
        }
        void keepStorage();
        await prune(db);
        onSaved?.();
      } catch {
        // No storage (private mode, blocked site data, a full disk): the edit isn't kept, so leaving asks again.
        storage = 'fails';
        const editor = useEditorStore.getState();
        if (editor.projectId === id) editor.setProjectSaved(false);
      }
    });
  };

  const flush = () => {
    if (timer) clearTimeout(timer);
    timer = null;
    if (pending) save(pending);
    pending = null;
  };

  // The editor is closing (or opening another video): what was waiting is written now, while it can still be read.
  const stopLeaving = onLeaveEdit(() => {
    if (timer) flush();
  });

  const unsubscribe = useEditorStore.subscribe((current, previous) => {
    if (current.load.status !== 'ready') return;
    const changed =
      current.history.present !== previous.history.present ||
      current.assets !== previous.assets ||
      current.privacy.tracksByAsset !== previous.privacy.tracksByAsset ||
      current.privacy.scans !== previous.privacy.scans ||
      current.privacy.covered !== previous.privacy.covered ||
      current.areas.thumbnails !== previous.areas.thumbnails ||
      current.transcription.captioned !== previous.transcription.captioned ||
      current.analyses !== previous.analyses ||
      current.load !== previous.load;
    if (!changed || !worthKeeping(current)) return;
    // Named right away, so closing the editor before the first write still keeps a recording.
    if (!current.projectId) {
      const created = now();
      current.setProjectId(`project-${created.toString(36)}-${Math.random().toString(36).slice(2, 6)}`, created);
      // Closing the editor writes it straight away, so there is nothing to lose by leaving.
      if (storage === 'works') useEditorStore.getState().setProjectSaved(true);
    }
    pending = useEditorStore.getState();
    if (timer) clearTimeout(timer);
    timer = setTimeout(flush, SAVE_DELAY_MS);
  });

  // Hiding the tab may be the last chance: a phone can drop it without closing it.
  const onHide = () => document.visibilityState === 'hidden' && flush();
  globalThis.document?.addEventListener('visibilitychange', onHide);
  globalThis.window?.addEventListener('pagehide', flush);
  return () => {
    flush();
    unsubscribe();
    stopLeaving();
    globalThis.document?.removeEventListener('visibilitychange', onHide);
    globalThis.window?.removeEventListener('pagehide', flush);
  };
}

/** Keeps at most {@link MAX_PROJECTS}: the oldest go, with any recording they kept. */
async function prune(db: ProjectDb): Promise<void> {
  const projects = await db.list();
  for (const project of projects.slice(MAX_PROJECTS)) {
    await db.remove(project.id);
    forgetFiles(project.id);
    if (project.recordingId) await removeProjectRecording(project.recordingId);
  }
}
